import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import SiteVisitCalendar from '../components/SiteVisitCalendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  Plus,
  Search,
  Filter,
  ChevronRight,
  AlertTriangle,
  Play,
  ClipboardCheck,
  Target,
  CalendarDays,
  List,
  Settings,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  scheduled: 'bg-sky-100 text-sky-700 border-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-300',
};

export default function SiteOperationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [calendarVisits, setCalendarVisits] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sites, setSites] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [calendarDateRange, setCalendarDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  // New visit form state
  const [newVisit, setNewVisit] = useState({
    site: '',
    visit_type_id: '',
    purpose: '',
    scope: '',
    planned_date: '',
    planned_time: '09:00',
    planned_duration: '4 hours',
    assigned_technician_id: '',
    assigned_technician_name: '',
    assigned_personnel: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [visitsRes, statsRes, assetsRes, typesRes, usersRes] = await Promise.all([
        axios.get(`${API}/site-visits`),
        axios.get(`${API}/dashboard/site-operations`),
        axios.get(`${API}/assets`),
        axios.get(`${API}/visit-types`),
        axios.get(`${API}/users`),
      ]);
      setVisits(visitsRes.data);
      setStats(statsRes.data);
      setVisitTypes(typesRes.data);
      
      // Extract unique sites
      const uniqueSites = [...new Set(assetsRes.data.map((a) => a.site))];
      setSites(uniqueSites);
      
      // Filter technicians (users who can execute visits)
      const techs = usersRes.data.filter(u => 
        ['org_user', 'org_admin', 'platform_admin'].includes(u.role) && u.status === 'active'
      );
      setTechnicians(techs);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCalendarData = useCallback(async () => {
    try {
      setCalendarLoading(true);
      const res = await axios.get(`${API}/site-visits/calendar`, {
        params: {
          start_date: calendarDateRange.start,
          end_date: calendarDateRange.end,
        }
      });
      setCalendarVisits(res.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [calendarDateRange]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarData();
    }
  }, [activeTab, fetchCalendarData]);

  const handleCreateVisit = async () => {
    try {
      const visitData = { ...newVisit };
      
      // If no template selected, send visit_type as the legacy string
      if (!newVisit.visit_type_id) {
        visitData.visit_type = 'routine_inspection';
      }
      
      await axios.post(`${API}/site-visits`, visitData);
      setShowCreateDialog(false);
      setNewVisit({
        site: '',
        visit_type_id: '',
        purpose: '',
        scope: '',
        planned_date: '',
        planned_time: '09:00',
        planned_duration: '4 hours',
        assigned_technician_id: '',
        assigned_technician_name: '',
        assigned_personnel: [],
      });
      fetchData();
      if (activeTab === 'calendar') {
        fetchCalendarData();
      }
    } catch (error) {
      console.error('Failed to create visit:', error);
    }
  };

  const handleVisitTypeChange = (typeId) => {
    const selectedType = visitTypes.find(t => t.id === typeId);
    setNewVisit({
      ...newVisit,
      visit_type_id: typeId,
      planned_duration: selectedType?.estimated_duration || '4 hours',
    });
  };

  const handleTechnicianChange = (techId) => {
    const tech = technicians.find(t => t.id === techId);
    setNewVisit({
      ...newVisit,
      assigned_technician_id: techId,
      assigned_technician_name: tech?.name || '',
      assigned_personnel: tech ? [tech.name] : [],
    });
  };

  const handleCalendarVisitClick = (visit) => {
    navigate(`/site-operations/${visit.id}`);
  };

  const handleCalendarDateSelect = (date) => {
    setNewVisit({
      ...newVisit,
      planned_date: date.toISOString().split('T')[0],
    });
    setShowCreateDialog(true);
  };

  const filteredVisits = visits.filter((visit) => {
    const matchesSearch =
      searchQuery === '' ||
      visit.visit_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.site?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.purpose?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || visit.status === statusFilter;
    const matchesType = typeFilter === 'all' || visit.visit_type === typeFilter || visit.visit_type_id === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getTaskProgress = (visit) => {
    const tasks = visit.tasks || [];
    if (tasks.length === 0) return 0;
    const completed = tasks.filter((t) => t.status === 'completed').length;
    return Math.round((completed / tasks.length) * 100);
  };

  const isAdmin = ['superadmin', 'platform_admin', 'org_admin'].includes(user?.role);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Site Operations</h1>
            <p className="text-slate-500">Manage site visits, inspections, and field activities</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin/visit-templates')}
                data-testid="manage-templates"
              >
                <Settings className="w-4 h-4 mr-2" />
                Templates
              </Button>
            )}
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-visit">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Visit
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200" data-testid="stat-scheduled">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Scheduled Visits</span>
                <Calendar className="w-5 h-5 text-sky-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats?.visits?.scheduled || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Upcoming</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-in-progress">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">In Progress</span>
                <Play className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-amber-600">{stats?.visits?.in_progress || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Active now</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-actions">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Open Actions</span>
                <ClipboardCheck className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats?.actions?.open || 0}</p>
              <p className="text-xs text-red-600 mt-1">{stats?.actions?.overdue || 0} overdue</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-sla">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">SLA Compliance</span>
                <Target className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-emerald-600">{stats?.actions?.sla_compliance || 100}%</p>
              <Progress value={stats?.actions?.sla_compliance || 100} className="h-1.5 mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* View Toggle Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="list" className="flex items-center gap-2" data-testid="tab-list">
                <List className="w-4 h-4" />
                List View
              </TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-2" data-testid="tab-calendar">
                <CalendarDays className="w-4 h-4" />
                Calendar
              </TabsTrigger>
            </TabsList>

            {activeTab === 'list' && (
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search visits..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-[200px]"
                    data-testid="visit-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="type-filter">
                    <SelectValue placeholder="Visit Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {visitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* List View */}
          <TabsContent value="list">
            <Card className="border-slate-200" data-testid="visits-list">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Site Visits ({filteredVisits.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filteredVisits.length === 0 ? (
                  <EmptyState
                    icon={MapPin}
                    title="No visits scheduled"
                    description={searchQuery ? "Try adjusting your search or filters." : "Schedule a site visit to inspect your assets."}
                    actionLabel={!searchQuery ? "Schedule visit" : undefined}
                    onAction={!searchQuery ? () => setIsNewVisitOpen(true) : undefined}
                  />
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredVisits.map((visit) => {
                      const progress = getTaskProgress(visit);
                      return (
                        <Link
                          key={visit.id}
                          to={`/site-operations/${visit.id}`}
                          className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors"
                          data-testid={`visit-${visit.visit_number}`}
                        >
                          <div
                            className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center',
                              visit.status === 'in_progress'
                                ? 'bg-amber-100'
                                : visit.status === 'completed'
                                ? 'bg-emerald-100'
                                : 'bg-sky-100'
                            )}
                            style={{ borderLeftColor: visit.color, borderLeftWidth: '4px' }}
                          >
                            <MapPin
                              className={cn(
                                'w-5 h-5',
                                visit.status === 'in_progress'
                                  ? 'text-amber-600'
                                  : visit.status === 'completed'
                                  ? 'text-emerald-600'
                                  : 'text-sky-600'
                              )}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-semibold text-slate-900">
                                {visit.visit_number}
                              </span>
                              <Badge
                                variant="outline"
                                className="text-xs"
                                style={{ borderColor: visit.color, color: visit.color }}
                              >
                                {visit.visit_type}
                              </Badge>
                              <Badge variant="outline" className={cn('text-xs', statusColors[visit.status])}>
                                {visit.status.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900 mt-1">{visit.purpose}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {visit.site}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {visit.planned_date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {visit.planned_time || '09:00'} • {visit.planned_duration}
                              </span>
                              {visit.assigned_technician_name && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {visit.assigned_technician_name}
                                </span>
                              )}
                            </div>
                            {visit.tasks?.length > 0 && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                                  <span>
                                    {visit.tasks.filter((t) => t.status === 'completed').length}/{visit.tasks.length} tasks
                                  </span>
                                  <span>{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-1.5" />
                              </div>
                            )}
                            {visit.findings?.length > 0 && (
                              <div className="mt-2 flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-amber-50 text-amber-700 border-amber-300"
                                >
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {visit.findings.length} findings
                                </Badge>
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar View */}
          <TabsContent value="calendar">
            <SiteVisitCalendar
              visits={calendarVisits}
              loading={calendarLoading}
              onVisitClick={handleCalendarVisitClick}
              onDateSelect={handleCalendarDateSelect}
            />
          </TabsContent>
        </Tabs>

        {/* Create Visit Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle>Schedule Site Visit</DialogTitle>
              <DialogDescription>
                Create a new site visit. Select a template to auto-populate tasks and checks.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid gap-2">
                <Label htmlFor="site">Site *</Label>
                <Select value={newVisit.site} onValueChange={(v) => setNewVisit({ ...newVisit, site: v })}>
                  <SelectTrigger data-testid="new-visit-site">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site} value={site}>
                        {site}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="type">Visit Type (Template)</Label>
                <Select value={newVisit.visit_type_id} onValueChange={handleVisitTypeChange}>
                  <SelectTrigger data-testid="new-visit-type">
                    <SelectValue placeholder="Select visit type template" />
                  </SelectTrigger>
                  <SelectContent>
                    {visitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                          <span>{type.name}</span>
                          <span className="text-xs text-slate-500">({type.estimated_duration})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {newVisit.visit_type_id
                    ? `Tasks and checks will be auto-loaded from this template`
                    : 'No template selected - visit will have no pre-configured tasks'}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="purpose">Purpose *</Label>
                <Input
                  id="purpose"
                  value={newVisit.purpose}
                  onChange={(e) => setNewVisit({ ...newVisit, purpose: e.target.value })}
                  placeholder="e.g., Quarterly operational inspection"
                  data-testid="new-visit-purpose"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="scope">Scope</Label>
                <Textarea
                  id="scope"
                  value={newVisit.scope}
                  onChange={(e) => setNewVisit({ ...newVisit, scope: e.target.value })}
                  placeholder="Describe the scope of the visit..."
                  data-testid="new-visit-scope"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newVisit.planned_date}
                    onChange={(e) => setNewVisit({ ...newVisit, planned_date: e.target.value })}
                    data-testid="new-visit-date"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={newVisit.planned_time}
                    onChange={(e) => setNewVisit({ ...newVisit, planned_time: e.target.value })}
                    data-testid="new-visit-time"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Select
                    value={newVisit.planned_duration}
                    onValueChange={(v) => setNewVisit({ ...newVisit, planned_duration: v })}
                  >
                    <SelectTrigger data-testid="new-visit-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 hour">1 hour</SelectItem>
                      <SelectItem value="2 hours">2 hours</SelectItem>
                      <SelectItem value="4 hours">4 hours</SelectItem>
                      <SelectItem value="6 hours">6 hours</SelectItem>
                      <SelectItem value="8 hours">8 hours</SelectItem>
                      <SelectItem value="Full day">Full day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="technician">Assigned Technician</Label>
                <Select value={newVisit.assigned_technician_id} onValueChange={handleTechnicianChange}>
                  <SelectTrigger data-testid="new-visit-technician">
                    <SelectValue placeholder="Select technician" />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.name} ({tech.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateVisit}
                disabled={!newVisit.site || !newVisit.purpose || !newVisit.planned_date}
                data-testid="submit-visit"
              >
                Schedule Visit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
